// ═══════════════════════════════════════════════════════════════
//  Approval Workflow
//  • Pending approvals inbox
//  • Multi-level approval chain visualization
//  • Approve / Reject / Escalate with comments
//  • Full audit trail per record
//  • Status timeline per submission
// ═══════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import LoaderComponent from "../../../../libraries/loader/loader";
import {
  PBExecuteQuery, PBCreateData, PBUpdateData,
  Q, userMeta, fmtDate,
} from "../../../../services/process/PlanningBudgeting/planningBudgetingService";

const STATUS_COLORS = {
  Pending:   { bg: "#fff8e1", text: "#e67e22", border: "#f0c040" },
  Approved:  { bg: "#e8f5ee", text: "#2d8c4e", border: "#a8d5b5" },
  Rejected:  { bg: "#fdecea", text: "#c0392b", border: "#f5a9a0" },
  Escalated: { bg: "#f0f4ff", text: "#4969ab", border: "#a0b4e0" },
  Draft:     { bg: "#f5f5f5", text: "#888",    border: "#ddd"    },
};

const StatusBadge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.Draft;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 12,
      background: s.bg, color: s.text, border: `1px solid ${s.border}`,
    }}>{status}</span>
  );
};

const SECTION = ({ title, children, action }) => (
  <div style={{
    background: "#fff", borderRadius: 14,
    boxShadow: "0 2px 14px rgba(73,105,171,0.09)",
    padding: "20px 22px", marginBottom: 22,
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
      <div style={{ fontWeight: 700, fontSize: 15, color: "#1a2340" }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

// ── Approval chain stepper ─────────────────────────────────────
const ApprovalChain = ({ levels = [], currentLevel }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 0, flexWrap: "wrap", margin: "10px 0" }}>
    {levels.map((level, i) => {
      const isDone    = i < currentLevel - 1;
      const isCurrent = i === currentLevel - 1;
      const color     = isDone ? "#2d8c4e" : isCurrent ? "#4969ab" : "#bbb";
      return (
        <React.Fragment key={i}>
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: "50%", border: `2px solid ${color}`,
              background: isDone ? "#2d8c4e" : isCurrent ? "#4969ab12" : "#f5f5f5",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 14, color: isDone ? "#fff" : color, fontWeight: 700,
            }}>
              {isDone ? "✓" : i + 1}
            </div>
            <div style={{ fontSize: 10, color, fontWeight: isCurrent ? 700 : 400, marginTop: 4, textAlign: "center", maxWidth: 80 }}>
              {level}
            </div>
          </div>
          {i < levels.length - 1 && (
            <div style={{
              flex: 1, height: 2, minWidth: 24,
              background: isDone ? "#2d8c4e" : "#e8ecf4",
              margin: "0 4px", marginBottom: 20,
            }} />
          )}
        </React.Fragment>
      );
    })}
  </div>
);

// ── Action modal ───────────────────────────────────────────────
const ActionModal = ({ record, onClose, onAction }) => {
  const [action,   setAction]   = useState("Approved");
  const [comments, setComments] = useState("");

  return (
    <>
      <div style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000,
      }} onClick={onClose} />
      <div style={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%,-50%)",
        background: "#fff", borderRadius: 16, padding: "28px 32px",
        zIndex: 1001, width: 480, boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
      }}>
        <div style={{ fontWeight: 800, fontSize: 17, color: "#1a2340", marginBottom: 6 }}>
          Review: {record.module_name}
        </div>
        <div style={{ fontSize: 12, color: "#aaa", marginBottom: 20 }}>
          Submitted by {record.submitted_by_name} · Level {record.approval_level}
        </div>

        {/* Record meta */}
        <div style={{
          background: "#f5f7fb", borderRadius: 10, padding: "12px 16px",
          fontSize: 12, marginBottom: 18, lineHeight: 2,
        }}>
          <div><strong>Module:</strong> {record.module_name}</div>
          <div><strong>Record ID:</strong> {record.record_id}</div>
          <div><strong>Submitted:</strong> {record.submitted_on}</div>
          <div><strong>Current Level:</strong> {record.approval_level}</div>
        </div>

        {/* Action selector */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>Action</div>
          <div style={{ display: "flex", gap: 8 }}>
            {["Approved","Rejected","Escalated"].map((a) => (
              <button key={a} onClick={() => setAction(a)} style={{
                flex: 1, padding: "8px", borderRadius: 8, cursor: "pointer",
                border: `2px solid ${action === a ? STATUS_COLORS[a].text : "#e0e6f0"}`,
                background: action === a ? STATUS_COLORS[a].bg : "#fff",
                color: action === a ? STATUS_COLORS[a].text : "#888",
                fontWeight: action === a ? 700 : 400, fontSize: 13,
                transition: "all 0.15s",
              }}>{a}</button>
            ))}
          </div>
        </div>

        {/* Comments */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 6 }}>Comments</div>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add remarks, reasons, or instructions..."
            rows={3}
            style={{
              width: "100%", padding: "8px 12px", borderRadius: 8,
              border: "1px solid #d0d8ec", fontSize: 13,
              boxSizing: "border-box", resize: "vertical",
            }}
          />
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "8px 20px", borderRadius: 8, border: "1px solid #d0d8ec",
            background: "#fff", color: "#666", fontSize: 13, cursor: "pointer",
          }}>Cancel</button>
          <button onClick={() => onAction(action, comments)} style={{
            padding: "8px 24px", borderRadius: 8, border: "none",
            background: STATUS_COLORS[action]?.text || "#4969ab",
            color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer",
          }}>Confirm {action}</button>
        </div>
      </div>
    </>
  );
};

const MODULES = ["Strategic Planning","Operational Planning","Operating Budget",
                 "Capital Budget","Revenue Budget","Personnel Budget","Scenario","Rolling Forecast"];

export default function ApprovalWorkflow() {
  const [tab,       setTab]      = useState("inbox");
  const [pending,   setPending]  = useState([]);
  const [loading,   setLoading]  = useState(false);
  const [modal,     setModal]    = useState(null);
  const [qf,        setQf]       = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");
  const gridRef = useRef(null);

  // Submit new approval form
  const [submitForm, setSubmitForm] = useState({
    module_name: "Operating Budget", record_id: "",
    approval_level: 1, comments: "",
  });

  const [userCredentials] = useState(
    JSON.parse(sessionStorage.getItem("userCredentials") || "{}")
  );

  const fetchPending = useCallback(async () => {
    setLoading(true);
    try {
      const c = PBExecuteQuery();
      const res = await axios.post(
        c[1],
        { query: Q.pendingApprovals(userCredentials.selectedentity, userCredentials.user_id) },
        c[0]
      );
      setPending(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [userCredentials]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  // ── Submit new approval request ──────────────────────────────
  const handleSubmitRequest = async () => {
    if (!submitForm.record_id) { alert("Please enter a Record ID"); return; }
    const payload = {
      database: "planning_budgeting",
      table: "approval_workflow",
      formData: {
        ...submitForm,
        entity_id:           userCredentials.selectedentity,
        submitted_by:        userCredentials.user_id,
        submitted_on:        fmtDate(),
        current_approver_id: userCredentials.user_id,
        status:              "Pending",
        ...userMeta(userCredentials),
      },
    };
    try {
      const c = PBCreateData();
      await axios.post(c[1], payload, c[0]);
      alert("Approval request submitted!");
      setSubmitForm({ module_name: "Operating Budget", record_id: "", approval_level: 1, comments: "" });
      fetchPending();
      setTab("inbox");
    } catch { alert("Error submitting request"); }
  };

  // ── Handle approve / reject / escalate ─────────────────────
  const handleAction = async (action, comments) => {
    const rec = modal;
    const nextLevel = action === "Escalated" ? rec.approval_level + 1 : rec.approval_level;
    const payload = {
      schema_name: "planning_budgeting",
      table: "approval_workflow",
      set_column: {
        status:       action,
        comments,
        approval_level:      nextLevel,
        last_updated_on:     fmtDate(),
        last_updated_by:     userCredentials.user_name,
      },
      where: [{ id: rec.id }],
    };
    try {
      const c = PBUpdateData();
      await axios.post(c[1], payload, c[0]);
      alert(`Record ${action} successfully!`);
      setModal(null);
      fetchPending();
    } catch { alert("Error processing action"); }
  };

  // ── Filtered data ────────────────────────────────────────────
  const filteredData = pending.filter((r) =>
    statusFilter === "All" ? true : r.status === statusFilter
  );

  const counts = {
    Pending:   pending.filter((r) => r.status === "Pending").length,
    Approved:  pending.filter((r) => r.status === "Approved").length,
    Rejected:  pending.filter((r) => r.status === "Rejected").length,
    Escalated: pending.filter((r) => r.status === "Escalated").length,
  };

  const COL_DEFS = [
    { headerName: "Module",       field: "module_name",      sortable: true, filter: true, resizable: true },
    { headerName: "Record ID",    field: "record_id",        sortable: true, filter: true, width: 110 },
    { headerName: "Submitted By", field: "submitted_by_name",sortable: true, filter: true },
    { headerName: "Submitted On", field: "submitted_on",     sortable: true, filter: true },
    { headerName: "Level",        field: "approval_level",   sortable: true, filter: true, width: 80 },
    { headerName: "Status",       field: "status",           sortable: true, filter: true,
      cellRendererFramework: (p) => <StatusBadge status={p.value} /> },
    { headerName: "Comments",     field: "comments",         sortable: true, filter: true, flex: 1 },
    { headerName: "Action",       field: "action",           width: 100, pinned: "right",
      cellRendererFramework: (p) => (
        p.data.status === "Pending" ? (
          <button onClick={() => setModal(p.data)} style={{
            padding: "4px 12px", borderRadius: 6,
            background: "#4969ab", color: "#fff",
            border: "none", fontSize: 11, cursor: "pointer", fontWeight: 600,
          }}>Review</button>
        ) : <StatusBadge status={p.data.status} />
      ),
    },
  ];

  if (loading) return <LoaderComponent loaderOpen />;

  return (
    <div style={{ padding: "4px 2px" }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: "#1a2340", marginBottom: 4 }}>
        Approval Workflow
      </div>
      <div style={{ fontSize: 12, color: "#aaa", marginBottom: 18 }}>
        Multi-level approval chain for all planning & budgeting records
      </div>

      {/* ── Status summary cards ── */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
        {Object.entries(counts).map(([status, count]) => {
          const s = STATUS_COLORS[status];
          return (
            <div key={status}
              onClick={() => setStatusFilter(status === statusFilter ? "All" : status)}
              style={{
                minWidth: 120, borderRadius: 12, padding: "14px 18px",
                background: statusFilter === status ? s.bg : "#fff",
                border: `2px solid ${statusFilter === status ? s.border : "#e8ecf4"}`,
                cursor: "pointer", transition: "all 0.15s",
                boxShadow: "0 2px 8px rgba(73,105,171,0.07)",
              }}
            >
              <div style={{ fontSize: 10, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.6 }}>{status}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: s.text, lineHeight: 1.1 }}>{count}</div>
            </div>
          );
        })}
        <div
          onClick={() => setStatusFilter("All")}
          style={{
            minWidth: 120, borderRadius: 12, padding: "14px 18px",
            background: statusFilter === "All" ? "#f0f4ff" : "#fff",
            border: `2px solid ${statusFilter === "All" ? "#4969ab" : "#e8ecf4"}`,
            cursor: "pointer", boxShadow: "0 2px 8px rgba(73,105,171,0.07)",
          }}
        >
          <div style={{ fontSize: 10, color: "#aaa", textTransform: "uppercase", letterSpacing: 0.6 }}>Total</div>
          <div style={{ fontSize: 28, fontWeight: 900, color: "#4969ab", lineHeight: 1.1 }}>{pending.length}</div>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 0, marginBottom: 22, borderBottom: "2px solid #e8ecf4" }}>
        {[
          ["inbox",  `📥 Inbox (${counts.Pending})`],
          ["all",    "📋 All Records"],
          ["submit", "📤 Submit for Approval"],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: "8px 20px", border: "none", cursor: "pointer",
            background: tab === k ? "#4969ab" : "transparent",
            color: tab === k ? "#fff" : "#666",
            fontWeight: tab === k ? 700 : 400,
            fontSize: 13, borderRadius: "8px 8px 0 0", transition: "all 0.15s",
          }}>{lbl}</button>
        ))}
      </div>

      {/* ══ TAB: Inbox ══ */}
      {tab === "inbox" && (
        <div>
          {filteredData.filter((r) => r.status === "Pending").length === 0 ? (
            <div style={{
              textAlign: "center", padding: 60, color: "#bbb",
              background: "#fff", borderRadius: 14,
              boxShadow: "0 2px 14px rgba(73,105,171,0.07)",
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Your inbox is empty</div>
              <div style={{ fontSize: 12, marginTop: 6 }}>No pending approvals at this time</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {filteredData.filter((r) => r.status === "Pending").map((r) => (
                <div key={r.id} style={{
                  background: "#fff", borderRadius: 12,
                  boxShadow: "0 2px 10px rgba(73,105,171,0.09)",
                  padding: "16px 20px",
                  borderLeft: "4px solid #e67e22",
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  flexWrap: "wrap", gap: 12,
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#1a2340", marginBottom: 4 }}>
                      {r.module_name}
                      <span style={{ marginLeft: 8, fontSize: 11, color: "#aaa", fontWeight: 400 }}>
                        Record #{r.record_id}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                      Submitted by <strong>{r.submitted_by_name}</strong> on {r.submitted_on}
                    </div>
                    <ApprovalChain
                      levels={["Initiator","Manager","Finance Head","CFO/COO"]}
                      currentLevel={r.approval_level}
                    />
                    {r.comments && (
                      <div style={{ fontSize: 11, color: "#888", fontStyle: "italic", marginTop: 4 }}>
                        "{r.comments}"
                      </div>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleAction("Approved", "Approved via inbox")} style={{
                      padding: "8px 18px", borderRadius: 8,
                      background: "#2d8c4e", color: "#fff",
                      border: "none", fontSize: 12, cursor: "pointer", fontWeight: 700,
                    }}>✓ Approve</button>
                    <button onClick={() => setModal(r)} style={{
                      padding: "8px 18px", borderRadius: 8,
                      background: "#4969ab", color: "#fff",
                      border: "none", fontSize: 12, cursor: "pointer", fontWeight: 600,
                    }}>Review</button>
                    <button onClick={() => handleAction("Rejected", "Rejected via inbox")} style={{
                      padding: "8px 18px", borderRadius: 8,
                      background: "#fff", color: "#c0392b",
                      border: "1px solid #c0392b", fontSize: 12, cursor: "pointer", fontWeight: 600,
                    }}>✕ Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ══ TAB: All records ══ */}
      {tab === "all" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
            <input type="text" placeholder="Search records..."
              value={qf} onChange={(e) => { setQf(e.target.value); gridRef.current?.api?.setQuickFilter(e.target.value); }}
              style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #d0d8ec", fontSize: 12, width: 260 }} />
          </div>
          <div className="ag-theme-alpine" style={{ height: 500, width: "100%", borderRadius: 8 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredData}
              columnDefs={COL_DEFS}
              pagination paginationPageSize={12}
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
            />
          </div>
        </div>
      )}

      {/* ══ TAB: Submit ══ */}
      {tab === "submit" && (
        <div style={{ maxWidth: 560 }}>
          <SECTION title="📤 Submit Record for Approval">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ fontSize: 11, color: "#888", fontWeight: 600, display: "block", marginBottom: 3 }}>
                  Module
                </label>
                <select
                  value={submitForm.module_name}
                  onChange={(e) => setSubmitForm((p) => ({ ...p, module_name: e.target.value }))}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 13 }}
                >
                  {MODULES.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#888", fontWeight: 600, display: "block", marginBottom: 3 }}>
                  Record ID
                </label>
                <input
                  type="text"
                  value={submitForm.record_id}
                  onChange={(e) => setSubmitForm((p) => ({ ...p, record_id: e.target.value }))}
                  placeholder="e.g. 1042"
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 13, boxSizing: "border-box" }}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: "#888", fontWeight: 600, display: "block", marginBottom: 3 }}>
                  Approval Level
                </label>
                <select
                  value={submitForm.approval_level}
                  onChange={(e) => setSubmitForm((p) => ({ ...p, approval_level: Number(e.target.value) }))}
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 13 }}
                >
                  {[1,2,3,4].map((l) => <option key={l} value={l}>Level {l}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: "span 2" }}>
                <label style={{ fontSize: 11, color: "#888", fontWeight: 600, display: "block", marginBottom: 3 }}>
                  Comments / Notes
                </label>
                <textarea
                  value={submitForm.comments}
                  onChange={(e) => setSubmitForm((p) => ({ ...p, comments: e.target.value }))}
                  rows={3}
                  placeholder="Explain why this record needs approval..."
                  style={{ width: "100%", padding: "7px 10px", borderRadius: 7, border: "1px solid #d0d8ec", fontSize: 13, boxSizing: "border-box", resize: "vertical" }}
                />
              </div>
            </div>

            {/* Approval chain preview */}
            <div style={{ marginTop: 16, padding: "14px 16px", background: "#f5f7fb", borderRadius: 10 }}>
              <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 8 }}>Approval Chain Preview</div>
              <ApprovalChain
                levels={["Initiator","Manager","Finance Head","CFO/COO"]}
                currentLevel={submitForm.approval_level}
              />
            </div>

            <button onClick={handleSubmitRequest} style={{
              marginTop: 18, padding: "9px 28px", background: "#4969ab",
              color: "#fff", border: "none", borderRadius: 8,
              fontWeight: 700, cursor: "pointer", fontSize: 14,
            }}>Submit for Approval</button>
          </SECTION>
        </div>
      )}

      {/* ── Action modal ── */}
      {modal && (
        <ActionModal
          record={modal}
          onClose={() => setModal(null)}
          onAction={handleAction}
        />
      )}
    </div>
  );
}
